import { createContext, useContext, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { localMoment, type UserDto } from "@habitos/shared";
import { api, ApiError } from "./api";

interface AuthValue {
  user: UserDto;
  /** Today in the user's own time zone, which is what reminders follow too. */
  today: string;
}

const AuthContext = createContext<AuthValue | null>(null);

export function useMeQuery() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.me();
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: Infinity,
  });
}

export function AuthProvider({ user, children }: { user: UserDto; children: ReactNode }) {
  const today = localMoment(new Date(), user.timezone).date;
  return <AuthContext.Provider value={{ user, today }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth outside AuthProvider");
  return value;
}

export function useSetUser() {
  const client = useQueryClient();
  return (user: UserDto | null) => {
    if (!user) client.clear();
    client.setQueryData(["me"], user);
  };
}
